import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, ShieldCheck, QrCode, LogIn, LogOut, Clock, MapPin, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PinPad } from "@/components/PinPad";
import { QRCodeCanvas } from "qrcode.react";
import { Footer } from "@/components/Footer";
import api, { apiErr } from "@/lib/api";
import { fmtClock, fmtDate } from "@/lib/time";

const LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/wingman/da9c2719-2c74-4d76-b1c3-a7e2071c9ed9/attachments/72ffa52fcb5a4ae8bceb3e4a0c7a1cce_rosemount-joinery-logo-scaled.jpg";

const TypeBadge = ({ t }) =>
  t === "P" ? (
    <Badge className="bg-slate-700 text-white hover:bg-slate-700 rounded-sm">Permanent</Badge>
  ) : (
    <Badge variant="outline" className="border-slate-500 text-slate-600 rounded-sm">Casual</Badge>
  );

const getGeo = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { timeout: 5000 }
    );
  });

export default function ClockIn() {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinError, setPinError] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [forgot, setForgot] = useState(null); // { type, step }
  const [locId, setLocId] = useState(null);
  const [locName, setLocName] = useState(null);
  const [locations, setLocations] = useState([]);
  const [qrSel, setQrSel] = useState(null);
  const debounce = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([]));
  }, []);

  useEffect(() => {
    const l = new URLSearchParams(window.location.search).get("loc");
    if (l) {
      setLocId(l);
      setQrSel(l);
      api.get(`/locations/${l}`).then((r) => setLocName(r.data.name)).catch(() => setLocName(null));
    }
  }, []);

  const openQr = () => {
    setQrSel(locId || (locations[0] && locations[0].id) || null);
    setQrOpen(true);
  };

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/employees/search`, { params: { q: query } });
        setResults(data);
      } catch (e) {
        /* ignore */
      }
    }, 200);
  }, [query]);

  const openPin = (emp) => {
    setSelected(emp);
    setPin("");
  };

  const submitClock = async () => {
    if (pin.length !== 4) return;
    setBusy(true);
    setPinError("");
    const geo = await getGeo();
    try {
      const { data } = await api.post(`/clock`, {
        employee_id: selected.id,
        pin,
        lat: geo.lat,
        lng: geo.lng,
        location_id: locId,
      });
      if (data.action === "clock_in") {
        toast.success(`Clocked in — ${data.name}`, {
          description: data.late_arrival
            ? "Flagged as a late arrival."
            : data.early_start === "pending"
            ? "Early start — pending admin approval."
            : "Have a great shift!",
        });
      } else {
        toast.success(`Clocked out — ${data.name}`, { description: "See you next time!" });
      }
      setSelected(null);
      setQuery("");
      setResults([]);
    } catch (e) {
      const msg = apiErr(e);
      setPinError(msg);
      toast.error(msg);
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Rosemount Joinery" className="h-10 sm:h-12 w-auto object-contain" data-testid="brand-logo" />
          </div>
          <Button
            variant="outline"
            className="rounded-sm border-slate-300"
            onClick={() => nav("/admin")}
            data-testid="admin-login-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Admin Login
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Live clock */}
        <div className="mb-8 sm:mb-10">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium uppercase tracking-wide">Melbourne Time (AEST / AEDT)</span>
          </div>
          <div
            className="font-mono font-semibold text-5xl sm:text-6xl text-foreground tabular-nums"
            data-testid="live-clock"
          >
            {fmtClock(now)}
          </div>
          <div className="text-base sm:text-lg text-muted-foreground font-medium mt-1" data-testid="live-date">
            {fmtDate(now)}
          </div>
        </div>

        {locName && (
          <div className="mb-6 flex items-center gap-2 rounded-sm border border-red-600 bg-red-50 px-4 py-3" data-testid="location-banner">
            <MapPin className="w-5 h-5 text-red-600" />
            <span className="text-sm font-semibold text-foreground">Location: <span className="text-red-600">{locName}</span></span>
          </div>
        )}

        {/* Actions row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Button
            variant="outline"
            className="h-14 rounded-sm border-slate-300 text-base sm:flex-1"
            onClick={openQr}
            data-testid="scan-qr-btn"
          >
            <QrCode className="w-5 h-5 mr-2" /> Scan QR Code
          </Button>
          <Button
            variant="outline"
            className="h-14 rounded-sm border-slate-300 text-sm sm:flex-1"
            onClick={() => setForgot({ type: "in" })}
            data-testid="forgot-in-btn"
          >
            Forgot to Clock In
          </Button>
          <Button
            variant="outline"
            className="h-14 rounded-sm border-slate-300 text-sm sm:flex-1"
            onClick={() => setForgot({ type: "out" })}
            data-testid="forgot-out-btn"
          >
            Forgot to Clock Out
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white border border-border rounded-sm p-4 sm:p-6">
          <Label className="text-sm font-semibold text-foreground mb-2 block">
            Find your name to clock in or out
          </Label>
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Start typing your name…"
              className="pl-10 h-14 text-lg rounded-sm border-slate-300 focus-visible:ring-red-600"
              data-testid="employee-search-input"
            />
          </div>

          <div className="mt-3 space-y-2" data-testid="search-results">
            {query.trim() && results.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No matching employees.</p>
            )}
            {results.map((emp, i) => (
              <button
                key={emp.id}
                onClick={() => openPin(emp)}
                data-testid={`employee-result-${emp.id}`}
                style={{ animationDelay: `${i * 30}ms` }}
                className="animate-fade-up w-full flex items-center justify-between gap-3 p-4 rounded-sm border border-border bg-white hover:bg-slate-50 hover:border-red-600 transition-colors duration-150 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="font-heading font-semibold text-lg text-foreground">{emp.name}</span>
                  <TypeBadge t={emp.employment_type} />
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                    emp.clocked_in ? "text-red-600" : "text-slate-500"
                  }`}
                >
                  {emp.clocked_in ? (
                    <>
                      <LogOut className="w-4 h-4" /> Clock Out
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" /> Clock In
                    </>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* PIN dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setPinError(""); setPin(""); } }}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="pin-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {selected.name}
                  <TypeBadge t={selected.employment_type} />
                </DialogTitle>
                <DialogDescription>
                  Enter your 4-digit PIN to{" "}
                  <span className="font-semibold text-foreground">
                    {selected.clocked_in ? "clock out" : "clock in"}
                  </span>
                  .
                </DialogDescription>
              </DialogHeader>
              {pinError && (
                <div className="rounded-sm bg-red-50 border border-red-300 text-red-700 text-sm px-3 py-2 flex items-start gap-2" data-testid="pin-error-message">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{pinError}</span>
                </div>
              )}
              <div className="py-2">
                <PinPad value={pin} onChange={(v) => { setPin(v); if (pinError) setPinError(""); }} onComplete={() => {}} />
              </div>
              <Button
                disabled={pin.length !== 4 || busy}
                onClick={submitClock}
                className="h-14 text-lg rounded-sm bg-red-600 hover:bg-red-700"
                data-testid="confirm-clock-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {busy ? "Please wait…" : selected.clocked_in ? "Confirm Clock Out" : "Confirm Clock In"}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="rounded-sm max-w-sm text-center" data-testid="qr-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Entrance QR Code</DialogTitle>
            <DialogDescription>
              Scan this code with a phone camera to open the clock-in page for the selected location.
            </DialogDescription>
          </DialogHeader>

          {locations.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground" data-testid="qr-none">
              No QR locations have been set up yet. Ask your admin to create one in the QR Code Manager.
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-2">
              {locations.length > 1 && (
                <select
                  value={qrSel || ""}
                  onChange={(e) => setQrSel(e.target.value)}
                  data-testid="qr-location-select"
                  className="w-full h-11 rounded-sm border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-600"
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              )}
              {qrSel && (
                <>
                  <div className="bg-white p-4 border border-border rounded-sm" data-testid="qr-canvas-wrap">
                    <QRCodeCanvas value={`${window.location.origin}/?loc=${qrSel}`} size={240} level="H" includeMargin fgColor="#0f172a" />
                  </div>
                  <div className="font-heading font-semibold text-lg" data-testid="qr-location-name">
                    {(locations.find((l) => l.id === qrSel) || {}).name}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Forgot dialog */}
      <ForgotDialog forgot={forgot} onClose={() => setForgot(null)} />

      <Footer />
    </div>
  );
}

function ForgotDialog({ forgot, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [when, setWhen] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (forgot) {
      setQuery("");
      setResults([]);
      setSelected(null);
      setPin("");
      setWhen("");
      setReason("");
    }
  }, [forgot]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/employees/search`, { params: { q: query } });
        setResults(data);
      } catch (e) {}
    }, 200);
  }, [query]);

  const submit = async () => {
    if (!selected || pin.length !== 4 || !when) {
      toast.error("Select your name, PIN and the time.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/forgot`, {
        employee_id: selected.id,
        pin,
        type: forgot.type,
        datetime_local: when,
        reason,
      });
      toast.success("Submitted for admin approval", {
        description: "Your manager will review and approve this entry.",
      });
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!forgot} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="forgot-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            {forgot?.type === "in" ? "Forgot to Clock In" : "Forgot to Clock Out"}
          </DialogTitle>
          <DialogDescription>
            Submit the time you actually {forgot?.type === "in" ? "started" : "finished"}. An admin
            must approve it.
          </DialogDescription>
        </DialogHeader>

        {!selected ? (
          <div>
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type your name…"
              className="h-12 rounded-sm border-slate-300 focus-visible:ring-red-600"
              data-testid="forgot-search-input"
            />
            <div className="mt-2 space-y-2 max-h-48 overflow-auto">
              {results.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => setSelected(emp)}
                  data-testid={`forgot-result-${emp.id}`}
                  className="w-full text-left p-3 rounded-sm border border-border hover:border-red-600 hover:bg-slate-50 transition-colors duration-150 font-medium"
                >
                  {emp.name}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm font-semibold">{selected.name}</div>
            <div>
              <Label className="text-sm mb-1 block">Date & time (AEST)</Label>
              <Input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-12 rounded-sm border-slate-300"
                data-testid="forgot-datetime"
              />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Reason (optional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Phone was flat, forgot to scan"
                className="h-12 rounded-sm border-slate-300"
                data-testid="forgot-reason"
              />
            </div>
            <div>
              <Label className="text-sm mb-2 block">Enter your PIN</Label>
              <PinPad value={pin} onChange={setPin} />
            </div>
            <Button
              disabled={busy}
              onClick={submit}
              className="w-full h-12 rounded-sm bg-red-600 hover:bg-red-700"
              data-testid="forgot-submit-btn"
            >
              {busy ? "Submitting…" : "Submit for Approval"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
